import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { AnimatePresence } from 'motion/react';
import { useEffect } from 'react';

import { NotificationContainer } from '@components/NotificationContainer';
import { OptionExpiryModal } from '@components/OptionExpiryModal';
import { SideMenu } from '@components/SideMenu';
import { BottomPanelProvider } from '@contexts/BottomPanelContext';
import { NotificationProvider } from '@contexts/NotificationContext';
import { OptionsDataProvider } from '@contexts/OptionsDataContext';
import { useAccount } from '@hooks/useAccount';
import { useAccountService } from '@hooks/useAccountService';
import { useConnectionManager } from '@hooks/useConnectionManager';
import { NavigationProvider, useNavigation } from '@hooks/useNavigation';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useOptionExpiry } from '@hooks/useOptionExpiry';
import { usePrices } from '@hooks/usePrices';
import { SettingsProvider, useSettings } from '@hooks/useSettings';
import { UIProvider, useUI } from '@hooks/useUI';
import { useVolatilityUpdate } from '@hooks/useVolatilityUpdate';
import { DepositTray } from '@screens/deposit-tray/DepositTray';
import { LandingScreen } from '@screens/LandingScreen';
import { MarketScreen } from '@screens/market/MarketScreen';
import { MyPositionsScreen } from '@screens/MyPositionsScreen';
import { OfflineOverlay } from '@screens/OfflineOverlay';
import { OptionChartScreen } from '@screens/OptionChartScreen';
import { OptionsHomeScreen } from '@screens/OptionsHomeScreen';
import { ProfileScreen } from '@screens/ProfileScreen';
import { SettingsScreen } from '@screens/SettingsScreen';
import { SplashScreen } from '@screens/SplashScreen';
import { SuccessScreen } from '@screens/SuccessScreen';

import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60, // 1 hour
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
});

interface Position {
  id: string;
  isCall: boolean;
  strikePrice: number;
  quantity: number;
  value: number;
  roi: number;
  purchasePrice: number;
  optionType: string;
  asset: 'ETH' | 'BTC' | 'SOL' | 'XRP';
  openedAtMs?: number;
  // demo settlement: expires at next midnight UTC
  expiresAtMs?: number;
}

function AppContent() {
  const { showSideMenu, showDepositTray, closeDepositTray } = useUI();
  const { currentPage, navigate } = useNavigation();
  const {
    advancedMode,
    setAdvancedMode,
    extraMode,
    setExtraMode,
    roundDuration,
    setRoundDuration,
    timeshiftOffsetHours,
    setTimeshiftOffsetHours,
  } = useSettings();
  const { positions } = useAccount();
  const isOnline = useOnlineStatus();
  const accountService = useAccountService();
  const { currentExpiryEvent, dismissExpiryEvent } = useOptionExpiry();

  // Initialize connection manager for background/foreground handling
  const { isRecovering } = useConnectionManager();

  // Initialize volatility updates
  useVolatilityUpdate();

  // Bootstrap price service on app load (ensures it runs regardless of which page loads first)
  usePrices();

  const getAssetFromPage = (page: string): Position['asset'] =>
    page === 'btcmarket'
      ? 'BTC'
      : page === 'solmarket'
        ? 'SOL'
        : page === 'xrpmarket'
          ? 'XRP'
          : 'ETH';

  useEffect(() => {
    accountService.init();
  }, [accountService]);

  const totalPositions = positions.length;

  return (
    <div className="h-full w-full flex flex-col overflow-x-hidden">
      {currentPage !== 'landing' && currentPage !== 'success' && currentPage !== 'splash' && <SideMenu />}
      <AnimatePresence mode="wait">
        {currentPage === 'landing' && <LandingScreen />}
        {currentPage === 'splash' && <SplashScreen onNavigate={navigate} />}
        {currentPage === 'success' && <SuccessScreen />}
        {currentPage === 'positions' && (
          <MyPositionsScreen
            advancedMode={advancedMode}
            onNavigate={navigate}
          />
        )}
        {currentPage === 'profile' && (
          <ProfileScreen
            showSideMenu={showSideMenu}
            toggleSideMenu={() => {}}
            totalPositions={totalPositions}
            onNavigate={navigate}
            advancedMode={advancedMode}
          />
        )}
        {currentPage === 'settings' && (
          <SettingsScreen
            showSideMenu={showSideMenu}
            toggleSideMenu={() => {}}
            advancedMode={advancedMode}
            setAdvancedMode={setAdvancedMode}
            totalPositions={totalPositions}
            onNavigate={navigate}
            extraMode={extraMode}
            setExtraMode={setExtraMode}
            roundDuration={roundDuration}
            setRoundDuration={setRoundDuration}
            timeshiftOffsetHours={timeshiftOffsetHours}
            setTimeshiftOffsetHours={setTimeshiftOffsetHours}
          />
        )}
        {(currentPage === 'btcmarket' ||
          currentPage === 'ethmarket' ||
          currentPage === 'solmarket' ||
          currentPage === 'xrpmarket') && (
          <MarketScreen
            asset={getAssetFromPage(currentPage)}
            showSideMenu={showSideMenu}
            toggleSideMenu={() => {}}
            advancedMode={advancedMode}
            totalPositions={totalPositions}
            onNavigate={navigate}
            isRecovering={isRecovering}
          />
        )}
        {currentPage === 'optionshome' && (
          <OptionsHomeScreen onNavigate={navigate} />
        )}
        {currentPage === 'optionchart' && (
          <OptionChartScreen onNavigate={navigate} />
        )}
      </AnimatePresence>
      {currentPage !== 'landing' && (
        <>
          {/* Global Notification Container */}
          <NotificationContainer />

          {/* Option Expiry Modal */}
          {currentExpiryEvent && (
            <OptionExpiryModal
              isOpen={!!currentExpiryEvent}
              onClose={dismissExpiryEvent}
              expiryEvent={currentExpiryEvent}
            />
          )}

          {/* Global Deposit Tray */}
          <DepositTray isOpen={showDepositTray} onClose={closeDepositTray} />

          {/* Offline Warning Overlay */}
          <OfflineOverlay isOffline={!isOnline} />
        </>
      )}
    </div>
  );
}

export default function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <NotificationProvider>
        <UIProvider>
          <SettingsProvider>
            <NavigationProvider>
              <OptionsDataProvider>
                <BottomPanelProvider>
                  <AppContent />
                </BottomPanelProvider>
              </OptionsDataProvider>
            </NavigationProvider>
          </SettingsProvider>
        </UIProvider>
      </NotificationProvider>
    </PersistQueryClientProvider>
  );
}
